import { Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import {
  NON_EDITABLE_RECOGNITION_STATUS,
  MAX_ALLOWED_CONTACT_COUNT_FCC,
  PAYMENT_METHOD_TYPES,
  SOURCE_TYPES,
  CONTACT_TYPE,
  FILTER_BY_GIFT_STATUS,
  FILTER_BY_GIFT_STATUS_NUMBER_TYPE
} from '@hero/constants';
import { useSendRemindersNotification } from '@hero/hooks';
import { UserName } from '@hero/molecules';
import { ContactBGSModal, ContactsEditModal, OrderTable } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import {
  useGetRecognitionOrders,
  useGetRecognition,
  useUpdateRecognition,
  useSendRemindersRecognition,
  useGetRecognitionRecipientList,
  useRecogRecipientsBulkUpsert,
  useRecogRecipientsDelete,
  recognitionServices
} from '@hero/services/order-service';
import { useNotification } from '@hero/store/hooks';
import { cleanObjectEmptyFields, createQueryKey } from '@hero/utils';
import { removeRecipientSchema, removeRecipientsSchema } from '@hero/validations';

import * as S from './style';

function OrdersTemplate() {
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const { showNotification, showSuccess } = useNotification();
  const [selectedRows, setSelectedRows] = useState([]);
  const queryClient = useQueryClient();

  const [isContactsEditModalOpen, setIsContactsEditModalOpen] = useState(false);
  const [globalFilters, setGlobalFilters] = useState({});
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [contactBGSModal, setContactBGSModal] = useState(false);

  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const { mutateAsync: updateRecognition } = useUpdateRecognition();
  const { data: recognition } = useGetRecognition({ recognitionCode });
  const { mutateAsync: recipientBulkUpsert } = useRecogRecipientsBulkUpsert();
  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    {
      code: recognition?.code,
      options: {
        select: 'contact.id',
        pagination: false
      }
    },
    {
      enabled: !!recognition?.code
    }
  );
  const { mutateAsync: deleteRecipient } = useRecogRecipientsDelete();

  const recognitionRecipientContactIds = useMemo(() => {
    return (
      recognitionRecipients?.docs.map(doc => doc?.contact?.id).filter(contactId => contactId) || []
    );
  }, [recognitionRecipients]);

  const params = useMemo(
    () => ({
      filters: {
        searchTerm: globalFilters?.searchTerm,
        giftStatus: globalFilters?.filters?.map(filter => filter.value),
        recognitionId: recognition?.id
      },
      pagination: {
        limit: pageSize,
        page: pageIndex + 1
      }
    }),
    [pageSize, pageIndex, recognition?.id, globalFilters]
  );

  const {
    data: ordersData,
    isLoading,
    refetch,
    isRefetching
  } = useGetRecognitionOrders(params, {
    enabled: !!recognition?.id
  });

  const isEditable = React.useMemo(
    () => !NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
    [recognition]
  );

  const updateContacts = async contactIds => {
    await recipientBulkUpsert({
      params: {
        code: recognition.code
      },
      contactIds,
      recipientContactIds: recognitionRecipientContactIds
    });

    await updateRecognition({
      params: { recognitionCode },
      body: {
        contacts: {
          type: 'list'
        }
      },
      successMessage: t('recipientsUpdatedSuccessfully')
    });

    refetch();
  };

  const onDeleteModal = (recipient, onSuccess) => {
    showNotification({
      title: t('areYouSureRemoveRecipient'),
      message: t('areYouSureRemoveRecipientMessage', { feature: 'oneTimeGift' }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('removeRecipientModalConfirmLabel')}
            </Typography>
          ),
          type: 'text'
        }
      ],
      formValidationSchema: removeRecipientSchema,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          await deleteRecipient({
            code: recognition.code,
            emails: [recipient?.email]
          });
          showSuccess({
            message: t('recipientRemovedSuccessfully'),
            type: 'toast'
          });
          if (onSuccess) onSuccess();
        }
      }
    });
  };

  const onDeleteMultiModal = (onSuccess = () => {}) => {
    showNotification({
      title: t('areYouSureRemoveRecipients'),
      message: t('areYouSureRemoveRecipientsMessage'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('removeRecipientsModalConfirmLabel')}
            </Typography>
          ),
          type: 'text'
        }
      ],
      formValidationSchema: removeRecipientsSchema,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          const searchTerm = globalFilters.searchTerm;
          const orderIds = Object.keys(selectedRows);

          const params = {
            filters: {
              searchTerm,
              type: 'recognition',
              recognitionCode,
              id: orderIds,
              status: {
                accepted: false
              }
            },
            options: {
              select: 'recipient.contact.id'
            }
          };

          const data = await queryClient.fetchQuery(createQueryKey('order', params), () => {
            return recognitionServices.orders(cleanObjectEmptyFields(params));
          });

          const contactIds = data.docs.map(doc => doc.recipient.contact.id);

          await deleteRecipient({
            code: recognitionCode,
            contactRefIds: contactIds
          });
          onSuccess();
          showSuccess({
            message: (
              <Trans
                i18nKey="recipientSuccessfullyRemoved"
                components={{ b: <b /> }}
                count={contactIds.length}
              />
            ),
            type: 'toast'
          });
        }
      }
    });
  };

  const isButtonDisabled = React.useMemo(
    () => NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
    [recognition]
  );

  const { send } = useSendRemindersNotification({ useSendReminder: useSendRemindersRecognition });

  const isReminderActive = useMemo(() => {
    const totalRecipients = recognition?.acceptanceRates?.total;
    const totalAcceptedRecipients = recognition?.acceptanceRates?.accepted;

    if (totalRecipients === 0) return false;
    if (totalAcceptedRecipients === 0) return true;

    return totalRecipients - totalAcceptedRecipients > 0;
  }, [recognition]);

  const isTeams = recognition?.source === SOURCE_TYPES.teams_app;

  async function onToggleSelectAll(table) {
    const searchTerm = globalFilters.searchTerm;

    const params = {
      filters: {
        searchTerm,
        type: 'recognition',
        recognitionCode,
        status: {
          accepted: false
        }
      },
      options: {
        select: 'id'
      }
    };

    const data = await queryClient.fetchQuery(createQueryKey('orders', params), () => {
      return recognitionServices.orders(cleanObjectEmptyFields(params));
    });

    if (data?.docs?.length)
      table.setRowSelection(data.docs.reduce((result, doc) => ({ ...result, [doc.id]: true }), {}));
  }

  return (
    <S.Recipients>
      <S.RecipientsHeader>
        <S.RecipientHeaderLeft>
          <S.RecipientHeaderText>
            <Trans
              i18nKey="totalRecipients"
              values={{ count: ordersData?.totalDocs || 0 }}
              components={{ b: <b /> }}
            />
          </S.RecipientHeaderText>
          {recognition?.contacts?.type === CONTACT_TYPE.list && !isTeams && (
            <>
              <S.RecipientsHeaderDivider />
              <Button variant="link" onClick={refetch}>
                <Icon
                  name="refresh"
                  color="var(--color-accent-primary-default)"
                  disabled={isButtonDisabled}
                />
                {isLoading || isRefetching ? `${t('refreshing')}...` : t('refresh')}
              </Button>
              {isReminderActive && (
                <>
                  <S.RecipientsHeaderDivider />
                  <Button
                    variant="link"
                    onClick={() => send(recognition?.code)}
                    disabled={isButtonDisabled}
                  >
                    <Icon name="email" color="var(--color-accent-primary-default)" />
                    {t('remindRecipients')}
                  </Button>
                </>
              )}
              <S.RecipientsHeaderDivider />
              <Button
                variant="link"
                disabled={!isEditable}
                onClick={() => {
                  setIsContactsEditModalOpen(true);
                }}
              >
                <Icon name="add" color="var(--color-accent-primary-default)" />
                {t('addRecipients')}
              </Button>
            </>
          )}
        </S.RecipientHeaderLeft>
      </S.RecipientsHeader>

      {recognition?.id && (
        <OrderTable
          data={ordersData?.docs}
          globalFilters={globalFilters}
          setGlobalFilters={setGlobalFilters}
          pagination={{
            pageIndex,
            pageSize,
            total: ordersData?.totalDocs
          }}
          customState={{
            onToggleSelectAll
          }}
          onChangeSelectedRows={selectedRows => setSelectedRows(selectedRows)}
          onRemoveSelectedRows={onDeleteMultiModal}
          isLoading={isLoading}
          pageCount={ordersData?.totalPages}
          setPagination={setPagination}
          renderNameCell={({ cell: { getValue } }) => {
            const { recipient = {}, code } = getValue();
            return (
              <UserName
                firstName={recipient?.firstName}
                lastName={recipient?.lastName}
                as={Link}
                to={ROUTE_PATHS.RECOGNITION_RECIPIENT_DETAIL(recognitionCode, code)}
                hasNameAvatar
              />
            );
          }}
          filterOptions={
            recognition?.contacts?.type === CONTACT_TYPE.list
              ? FILTER_BY_GIFT_STATUS
              : FILTER_BY_GIFT_STATUS_NUMBER_TYPE
          }
          actionButtons={{
            ...(recognition?.contacts?.type === CONTACT_TYPE.list && {
              delete: {
                onClick: onDeleteModal
              }
            })
          }}
        />
      )}
      {recognition && isContactsEditModalOpen && (
        <ContactsEditModal
          onNextReviewAndConfirmStep={({ onNext, numberOfContacts }) => {
            const maxAllowedContactFCC = merchantData.configs[MAX_ALLOWED_CONTACT_COUNT_FCC];

            if (
              maxAllowedContactFCC < numberOfContacts &&
              recognition.paymentMethod.type !== PAYMENT_METHOD_TYPES.houseAccount
            ) {
              return setContactBGSModal(true); // now allowed for now
            } else {
              onNext();
            }
          }}
          isModalOpen={isContactsEditModalOpen}
          onRequestClose={() => setIsContactsEditModalOpen(false)}
          updateContacts={updateContacts}
          selectedContactIds={recognitionRecipientContactIds || []}
          isLiveFeature
          isCCPayment={recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard}
        />
      )}
      <ContactBGSModal
        recognitionCode={recognitionCode}
        isModalOpen={contactBGSModal}
        onModalOpen={() => {
          setContactBGSModal(false);
        }}
        onSendSuccessfully={() => {
          setIsContactsEditModalOpen(false);
        }}
      />
    </S.Recipients>
  );
}

export { OrdersTemplate };
